import { computed, ref, Ref, watch } from '@vue/composition-api';
import { asyncComputed, EventHookOn, toRefs } from '@vueuse/core';
import { filter, timer } from 'rxjs';
import { TurnPageOption } from '../Model/Issue';
import { ActionStore, IssueDrawerActionEdit } from './action';
import { useIntervalFn } from '@vueuse/core';

export interface TurnPageOptionsPart {
  checkNextAndPrevious?: (
    id: number,
    commentId?: any
  ) => {
    hasNext: boolean;
    hasPrevious: boolean;
  };
  nextIssue?: (
    id: number,
    commentId?: any
  ) => Promise<TurnPageOption | number | undefined>;
  previousIssue?: (
    id: number,
    commentId?: any
  ) => Promise<TurnPageOption | number | undefined>;
}

export const useTurnPage = (
  actionStore: ActionStore,
  onProjectChanged: EventHookOn<number>,
  onSprintChanged: EventHookOn<number | null>
) => {
  const hasNext = ref(false);
  const hasPrevious = ref(false);

  const refreshPagedStatus = async (clear = true) => {
    if (clear) {
      hasNext.value = false;
      hasPrevious.value = false;
    }
    if (
      actionStore.action.value?.type === 'Edit' &&
      actionStore.turnPageOptions.value?.checkNextAndPrevious
    ) {
      const res = await Promise.resolve(
        actionStore.turnPageOptions.value.checkNextAndPrevious(
          actionStore.action.value.issueId,
          actionStore.action.value.extendsData?.commentId
        )
      );
      if (res) {
        if (
          !clear &&
          (hasNext.value !== !!res.hasNext ||
            hasPrevious.value !== !!res.hasPrevious)
        ) {
          pause();
        }

        hasNext.value = !!res.hasNext;
        hasPrevious.value = !!res.hasPrevious;
      }
    }
  };

  const clearTurnPageStatus = () => {
    hasNext.value = false;
    hasPrevious.value = false;
    actionStore.turnPageOptions.value = undefined;
  };

  const { pause, resume } = useIntervalFn(() => {
    if (actionStore.action.value?.type === 'Edit') {
      refreshPagedStatus(false);
    }
  }, 1000);

  watch(
    () => actionStore.action.value,
    () => {
      refreshPagedStatus();
      resume();
    }
  );

  const getNextIssueInfo = async () => {
    if (
      hasNext.value &&
      actionStore.action.value?.type === 'Edit' &&
      actionStore.turnPageOptions.value?.nextIssue
    ) {
      return await actionStore.turnPageOptions.value.nextIssue(
        actionStore.action.value.issueId,
        actionStore.action.value.extendsData?.commentId
      );
    }
  };

  const getPreviousIssueInfo = async () => {
    if (
      hasPrevious.value &&
      actionStore.action.value?.type === 'Edit' &&
      actionStore.turnPageOptions.value?.previousIssue
    ) {
      return await actionStore.turnPageOptions.value.previousIssue(
        actionStore.action.value.issueId,
        actionStore.action.value.extendsData?.commentId
      );
    }
  };

  onProjectChanged(() => {
    clearTurnPageStatus();
  });

  onSprintChanged(() => {
    clearTurnPageStatus();
  });

  return {
    hasNext,
    hasPrevious,
    getNextIssueInfo,
    getPreviousIssueInfo
  };
};

export type TurnPageStore = ReturnType<typeof useTurnPage>;
